/**
 * Thin, TanStack-friendly DataGrid for the agent-first project surfaces.
 *
 * The shared ``@verantos-dev/shared-components`` ``DataGrid`` wrapper couples
 * the grid to an RTK-Query fetch hook (``useFetchDataGridData`` →
 * ``TypedUseQueryHookResult``). Our project surfaces fetch via TanStack Query,
 * so we render ``DataGridPro`` directly — but we keep **100% visual + behavioral
 * parity** with the shared wrapper by reusing its actual presentational pieces:
 * the bordered ``DataGridLayout`` shell, the custom ``DataGridPagination`` slot
 * (data-source-agnostic — it reads the grid API context), the ``DataGridOverlay``
 * empty states, and the identical footer / slotProps / cell styling. The only
 * difference from the wrapper is where the rows come from.
 *
 * Callers pass already-fetched ``rows`` + grid state (``loading``,
 * ``paginationMode``, ``rowCount``, ``paginationModel``, ``onPaginationModelChange``)
 * exactly as they would to a bare ``DataGridPro``.
 */

import { Box, Typography, useTheme } from '@mui/material';
import {
  DataGridPro,
  type DataGridProProps,
  type GridSlots,
  type GridValidRowModel,
  type PropsFromSlot,
} from '@mui/x-data-grid-pro';
import { LicenseInfo } from '@mui/x-license';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  DataGridLayout,
  DataGridOverlay,
  DataGridPagination,
} from '@verantos-dev/shared-components';
import { fontSize } from '@verantos-dev/shared-ui-components/tokens';

// Setting the license is idempotent and inexpensive; we duplicate the call
// here (mirroring ``ProjectArtifactsDataGrid``) so this grid renders licensed
// regardless of which other shared-components surfaces happen to load first.
LicenseInfo.setLicenseKey(
  '7ab0abc518f1ec6ce43e50d7d9280a94Tz0xMjA1NTgsRT0xNzkyMjgxNTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=' // pragma: allowlist secret
);

export interface ProjectDataGridProps<T extends GridValidRowModel>
  extends Omit<DataGridProProps<T>, 'loading'> {
  /** Stable id — used for the layout + grid test ids. */
  id: string;
  /** Whether the grid is loading (drives the shared linear-progress overlay). */
  loading?: boolean;
  /** Overlay copy when there are zero rows. Falls back to the shared default. */
  noRowsLabel?: string;
  /** Overlay copy when a filter excludes all rows. Falls back to the shared default. */
  noResultsLabel?: string;
  /** ``sx`` for the surrounding {@link DataGridLayout} shell (e.g. ``maxHeight``). */
  layoutSx?: DataGridProProps['sx'];
}

/**
 * ``DataGridPro`` composed with the shared wrapper's presentational slots so it
 * is visually identical to every other grid in the app, while sourcing rows
 * from TanStack Query rather than RTK.
 */
export const ProjectDataGrid = <T extends GridValidRowModel>({
  id,
  loading,
  noRowsLabel,
  noResultsLabel,
  layoutSx,
  slots,
  slotProps,
  sx,
  pageSizeOptions = [10, 25, 50, 100],
  ...rest
}: ProjectDataGridProps<T>) => {
  const theme = useTheme();
  const intl = useIntl();

  // Mirror the shared DataGrid wrapper's slots verbatim: custom pagination,
  // the "showing X of Y" footer count, and the centered empty-state overlays.
  const mergedSlots = useMemo(
    () => ({
      pagination: DataGridPagination as PropsFromSlot<GridSlots['pagination']>,
      footerRowCount: ({
        rowCount,
        visibleRowCount,
      }: {
        rowCount: number;
        visibleRowCount: number;
      }) => (
        <Box mx={theme.spacing(3)} display="flex" alignItems="center">
          <Typography variant="body2" sx={{ fontSize: fontSize[12] }}>
            {visibleRowCount !== rowCount
              ? intl.formatMessage(
                  { id: 'datagrid.footer.rows.totalRowsFiltered' },
                  { visibleRow: visibleRowCount, totalRows: rowCount }
                )
              : intl.formatMessage(
                  { id: 'datagrid.footer.rows.totalRows' },
                  { totalRows: rowCount }
                )}
          </Typography>
        </Box>
      ),
      noRowsOverlay: () => (
        <DataGridOverlay>
          {noRowsLabel ?? intl.formatMessage({ id: 'dataGrid.overlay.noRows' })}
        </DataGridOverlay>
      ),
      noResultsOverlay: () => (
        <DataGridOverlay>
          {noResultsLabel ?? intl.formatMessage({ id: 'dataGrid.overlay.noResults' })}
        </DataGridOverlay>
      ),
      ...slots,
    }),
    [intl, theme, noRowsLabel, noResultsLabel, slots]
  );

  return (
    <DataGridLayout id={id} sx={layoutSx}>
      <DataGridPro
        data-testid={`${id}-grid`}
        disableRowSelectionOnClick
        pageSizeOptions={pageSizeOptions}
        {...rest}
        loading={loading}
        slots={mergedSlots}
        slotProps={{
          loadingOverlay: {
            variant: 'linear-progress',
            noRowsVariant: 'skeleton',
          },
          ...slotProps,
        }}
        sx={{
          minHeight: '400px',
          flex: 1,
          border: 'none',
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
          ...sx,
        }}
      />
    </DataGridLayout>
  );
};
